/**
 * @copyright (C) Microsoft Corporation.
 * All Rights Reserved.
 */
import React, { useState } from "react";
import { Text, MessageBarType, getTheme } from "@fluentui/react";
import {
    HuxActionCard,
    HuxInfoItemList,
    HuxFieldType,
    IHuxLinkProps,
    HuxConfirmDialog,
    IHuxMessageProps,
    postMessage
} from "@nuance/hux-components";
import {
    WorkflowSvc,
    WORKFLOW_SVC_CACHE_KEYS,
    useHapAuth,
    useApiRequest,
    convertToLocaleDateTime,
    IEntraLinkingInfoDTO,
    IEntraLinkingStatusDTO,
    EntraLinkingStatus
} from "@nuance/hap-components";
import { useTranslation } from "react-i18next";
import { getLogger } from "@nuance/hux-diagnostics";
import { EventTypes } from "../EventTypes";
import { Query, useQueryClient } from "@tanstack/react-query";
import { EntraLinkingProgressAndMessage } from "./EntraLinkingProgressAndMessage";
import { EntraLinkingDetailsPanel } from "./EntraLinkingDetailsPanel";

const logger = getLogger();

/**
 * Props for the entra linking card
 */
export interface IEntraLinkingCardProps {
    entraLinkingInfo: IEntraLinkingInfoDTO;
    orgName: string;
    groupNames?: string;
}

/**
 * Styling for the left side column
 */
const leftColumnStyles: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    flex: 2,
    gap: "12px"
};

/**
 * Styling for the right side column
 */
const rightColumnStyles: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    flex: 1
};

/**
 * Card displaying information for a entra linking task
 * @param props IEntraLinkingCardProps
 * @returns JSX.Element
 */
export const EntraLinkingCard = (props: IEntraLinkingCardProps) => {
    const theme = getTheme();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { userContext } = useHapAuth();
    const [entraLinkingInfo, setEntraLinkingInfo] = useState<
        IEntraLinkingInfoDTO | IEntraLinkingStatusDTO
    >(props.entraLinkingInfo);
    const [isRemoveEntraLinkingDialogOpen, setIsRemoveEntraLinkingDialogOpen] = useState(false);
    const [isCancelEntraLinkingDialogOpen, setIsCancelEntraLinkingDialogOpen] = useState(false);
    const [isViewDetailsPanelOpen, setIsViewDetailsPanelOpen] = useState(false);
    const [linkingIsRunning, setLinkingIsRunning] = useState(false);

    const workflowSvc = new WorkflowSvc(userContext);

    const entraLinkingStatusReq = useApiRequest(
        workflowSvc.getEntraLinkingStatus(props.entraLinkingInfo.entraLinkingTaskGuid, true),
        {
            refetchInterval: (query: Query<IEntraLinkingStatusDTO>) => {
                const linkingData = query.state.data as IEntraLinkingStatusDTO;
                if (
                    linkingData &&
                    (linkingData.status === EntraLinkingStatus.Running ||
                        linkingData.status === EntraLinkingStatus.NotStarted)
                ) {
                    if (!linkingIsRunning) setLinkingIsRunning(true);
                    return 2000;
                } else {
                    if (linkingIsRunning) {
                        setLinkingIsRunning(false);
                    }
                    return false;
                }
            }
        }
    );

    /**
     * If the entra linking returns with different data, set the entraLinkingInfo to that
     */
    if (
        entraLinkingStatusReq.data &&
        JSON.stringify(entraLinkingStatusReq.data) !== JSON.stringify(entraLinkingInfo)
    )
        setEntraLinkingInfo(entraLinkingStatusReq.data);

    /**
     * CSS styling for the card skeleton.
     */
    const cardStyles: React.CSSProperties = {
        backgroundColor: "#FFFFFF",
        flexGrow: 1,
        boxShadow: theme.effects.elevation8,
        borderRadius: "2px",
        padding: "24px 24px 0px 24px",
        display: "flex",
        flexDirection: "row",
        gap: "12px"
    };

    const errorCount = (entraLinkingInfo as IEntraLinkingStatusDTO).mappings?.filter(
        m => m.hasError
    ).length;
    const mappedCount = (entraLinkingInfo as IEntraLinkingStatusDTO).mappings?.filter(
        m => m.isCompleted && !m.hasError
    ).length;
    /** List of information to be shown */
    const infoItemListFields = [
        {
            label: t("EntraUserLink.Mapping_Identifier_Label"),
            type: HuxFieldType.Text,
            value: props.entraLinkingInfo.entraLinkingTaskGuid
        },
        {
            label: t("EntraUserLink.Organization_Label"),
            type: HuxFieldType.Text,
            value: props.orgName
        }
    ];

    if (props.groupNames) {
        infoItemListFields.push({
            label: t("EntraUserLink.Groups_Label"),
            type: HuxFieldType.Text,
            value: props.groupNames
        });
    }
    if (entraLinkingInfo.status !== EntraLinkingStatus.NotStarted) {
        infoItemListFields.push({
            label: t("EntraUserLink.Users_To_Map_Label"),
            type: HuxFieldType.Text,
            value: Number(entraLinkingInfo.totalUsers).toString()
        });
        infoItemListFields.push({
            label: t("EntraUserLink.Users_With_Errors_Label"),
            type: HuxFieldType.Text,
            value:
                entraLinkingInfo.status != EntraLinkingStatus.Canceled
                    ? Number(errorCount ?? 0).toString()
                    : "0"
        });
    }

    /**
     * Buttons for the action card
     */
    const actionCardLinks: IHuxLinkProps[] = [];

    /**
     * Populate the action card links based on the status of the entra linking
     */
    if (
        entraLinkingInfo.status === EntraLinkingStatus.Running ||
        entraLinkingInfo.status === EntraLinkingStatus.NotStarted
    ) {
        actionCardLinks.push({
            name: t("EntraUserLink.Actions.Cancel_Mapping"),
            onClick: () => {
                logger.trackEvent(EventTypes.CancelMappingClicked, {
                    entraLinkingInfoTaskGuid: entraLinkingInfo.entraLinkingTaskGuid,
                    userGuid: userContext.user.guid
                });
                setIsCancelEntraLinkingDialogOpen(true);
            }
        });
    } else {
        actionCardLinks.push({
            name: t("EntraUserLink.Actions.View_Details"),
            onClick: () => {
                logger.trackEvent(EventTypes.ViewEntraMappingDetailsClicked, {
                    entraLinkingInfoTaskGuid: entraLinkingInfo.entraLinkingTaskGuid,
                    userGuid: userContext.user.guid
                });
                setIsViewDetailsPanelOpen(true);
            }
        });
        if (
            entraLinkingInfo.status === EntraLinkingStatus.Succeeded &&
            mappedCount &&
            mappedCount > 0
        ) {
            actionCardLinks.push({
                name: t("EntraUserLink.Actions.Export_Results"),
                onClick: () => {
                    logger.trackEvent(EventTypes.ExportEntraMappingResultsClicked, {
                        entraLinkingInfoTaskGuid: entraLinkingInfo.entraLinkingTaskGuid,
                        userGuid: userContext.user.guid
                    });
                    exportResultsAction();
                }
            });
        }
        if (
            entraLinkingInfo.status === EntraLinkingStatus.Succeeded &&
            errorCount &&
            errorCount > 0
        ) {
            actionCardLinks.push({
                name: t("EntraUserLink.Actions.Export_Failures"),
                onClick: () => {
                    logger.trackEvent(EventTypes.ExportEntraMappingFailuresClicked, {
                        entraLinkingInfoTaskGuid: entraLinkingInfo.entraLinkingTaskGuid,
                        userGuid: userContext.user.guid
                    });
                    exportFailuresAction();
                }
            });
        }
        actionCardLinks.push({
            name: t("EntraUserLink.Actions.Remove_Mapping"),
            onClick: () => {
                logger.trackEvent(EventTypes.RemoveEntraMappingClicked, {
                    entraLinkingInfoTaskGuid: entraLinkingInfo.entraLinkingTaskGuid,
                    userGuid: userContext.user.guid
                });
                setIsRemoveEntraLinkingDialogOpen(true);
            }
        });
    }

    /**
     * Actions that take place when removing a entra mapping
     * @returns Promise<string | undefined>
     */
    const removeMappingAction = async () => {
        return workflowSvc
            .removeEntraLinking(entraLinkingInfo.entraLinkingTaskGuid)
            .execute()
            .then(() => {
                /**
                 * Track the event
                 */
                logger.trackEvent(EventTypes.EntraLinkingRemoved, {
                    entraLinkingInfoTaskGuid: entraLinkingInfo.entraLinkingTaskGuid,
                    userGuid: userContext.user.guid
                });

                /**
                 * Success message props
                 */
                const msgProps: IHuxMessageProps = {
                    message: t("EntraUserLink.Removed_Success_Message"),
                    timeout: 5,
                    messageBarType: MessageBarType.success,
                    "aria-live": "assertive"
                };

                postMessage(msgProps);

                /**
                 * Invalidate the cache
                 */
                queryClient.invalidateQueries({
                    queryKey: [WORKFLOW_SVC_CACHE_KEYS.entraLinkingHistory, userContext.user.guid]
                });
                return undefined;
            })
            .catch(() => {
                logger.logError("Error removing entra mapping");
                return t("EntraUserLink.Removed_Failure_Message");
            });
    };

    /**
     * Actions that take place when cancelling a user import
     * @returns Promise<string | undefined>
     */
    const cancelImportAction = async () => {
        return workflowSvc
            .cancelEntraLinking(props.entraLinkingInfo.entraLinkingTaskGuid)
            .execute()
            .then(() => {
                /**
                 * Track the event
                 */
                logger.trackEvent(EventTypes.UserMappingCancelled, {
                    entraLinkingInfoTaskGuid: props.entraLinkingInfo.entraLinkingTaskGuid,
                    userGuid: userContext.user.guid
                });

                /**
                 * Success message props
                 */
                const msgProps: IHuxMessageProps = {
                    message: t("EntraUserLink.Cancelled_Success_Message"),
                    timeout: 5,
                    messageBarType: MessageBarType.success,
                    "aria-live": "assertive"
                };

                postMessage(msgProps);

                /**
                 * Invalidate the cache
                 */
                queryClient.invalidateQueries({
                    queryKey: [WORKFLOW_SVC_CACHE_KEYS.entraLinkingHistory, userContext.user.guid]
                });
                queryClient.invalidateQueries({
                    queryKey: [
                        WORKFLOW_SVC_CACHE_KEYS.entraLinking,
                        props.entraLinkingInfo.entraLinkingTaskGuid
                    ]
                });
                return undefined;
            })
            .catch(() => {
                logger.logError("Error cancelling entra mapping");
                return t("EntraUserLink.Cancelled_Failure_Message");
            });
    };

    /**
     * Actions that take place when exporting the results of a user mapping
     * @returns Promise<string | undefined>
     */
    const exportResultsAction = () => {
        const exportedData = (entraLinkingInfo as IEntraLinkingStatusDTO).mappings
            ? (entraLinkingInfo as IEntraLinkingStatusDTO).mappings
                  ?.filter(mapping => mapping.isCompleted && !mapping.hasError)
                  .map(mapping => {
                      return {
                          loginId: mapping.loginId,
                          entraOid: mapping.entraOid
                      };
                  })
            : [];
        const header = "NmsLogin,EntraObjectId\n";
        const data = exportedData?.map(row => Object.values(row).join(",")).join("\n");
        const blob = new Blob([header + data], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "NMSLoginToEntraMapping.csv";
        link.click();
        link.remove();
    };

    /**
     * Actions that take place when exporting the failures of a user mapping
     * @returns Promise<string | undefined>
     */
    const exportFailuresAction = () => {
        const exportedData = (entraLinkingInfo as IEntraLinkingStatusDTO).mappings
            ? (entraLinkingInfo as IEntraLinkingStatusDTO).mappings
                  ?.filter(mapping => mapping.isCompleted && mapping.hasError)
                  .map(mapping => {
                      return {
                          name: `"${mapping.lastName}, ${mapping.firstName}"`,
                          loginId: `"${mapping.loginId}"`,
                          emailAddress: `"${mapping.emailAddress}"`,
                          status: `"${mapping.description}"`
                      };
                  })
            : [];
        const header = `${t("EntraUserLink.Name_Head")},${t("EntraUserLink.Username_Head")},${t("EntraUserLink.Email_Head")},${t("EntraUserLink.Status_Head")}\n`;
        const data = exportedData?.map(row => Object.values(row).join(",")).join("\n");
        const blob = new Blob([header + data], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "NMSLoginToEntraMappingFailures.csv";
        link.click();
        link.remove();
    };

    return (
        <>
            <div style={cardStyles}>
                <div style={leftColumnStyles}>
                    <Text variant="xLarge">
                        {convertToLocaleDateTime(new Date(entraLinkingInfo.submittedAt))}
                    </Text>
                    <EntraLinkingProgressAndMessage
                        entraLinkingInfo={entraLinkingInfo}
                        messageBarButtonAction={() => {
                            setIsViewDetailsPanelOpen(true);
                        }}
                    />
                    <HuxInfoItemList fields={infoItemListFields} />
                </div>
                <div style={rightColumnStyles}>
                    <div
                        style={{
                            minWidth: "150px",
                            minHeight: "75px",
                            padding: "8px"
                        }}
                    >
                        <HuxActionCard links={actionCardLinks} ariaLabel="entraLinkingCardLinks" />
                    </div>
                </div>
            </div>
            {isRemoveEntraLinkingDialogOpen && (
                <HuxConfirmDialog
                    hidden={false}
                    title={t("EntraUserLink.Remove_Mapping_Title")}
                    children={<Text>{t("EntraUserLink.Remove_Mapping_Text")}</Text>}
                    action={{
                        description: t("Form.YesRemove_Button"),
                        onAction: removeMappingAction
                    }}
                    closeDialog={() => {
                        setIsRemoveEntraLinkingDialogOpen(false);
                    }}
                    confirmButtonText={t("Form.YesRemove_Button")}
                />
            )}
            {isCancelEntraLinkingDialogOpen && (
                <HuxConfirmDialog
                    hidden={false}
                    title={t("EntraUserLink.Cancel_Mapping_Title")}
                    children={<Text>{t("EntraUserLink.Cancel_Mapping_In_Progress_Text")}</Text>}
                    action={{
                        description: t("Progress.Spinner_Cancelling"),
                        onAction: cancelImportAction
                    }}
                    closeDialog={() => {
                        setIsCancelEntraLinkingDialogOpen(false);
                    }}
                    confirmButtonText={t("Form.YesCancel_Button")}
                />
            )}
            {isViewDetailsPanelOpen && (
                <EntraLinkingDetailsPanel
                    onDismiss={() => {
                        setIsViewDetailsPanelOpen(false);
                    }}
                    entraLinkingDetails={entraLinkingInfo as IEntraLinkingStatusDTO}
                    orgGuid={props.entraLinkingInfo.organizationGuid}
                    orgName={props.orgName}
                    groupNames={props.groupNames}
                />
            )}
        </>
    );
};
